import https, { RequestOptions } from 'https';
import { PeerCertificate, TLSSocket } from 'tls';
import { parse as parseURL, UrlWithStringQuery } from 'url';

import nodeFetch from '@meshwatch/node-fetch';
import {
  CertificateCheckResult,
  ExecuteMonitorParamsDTO,
  ExecutorCheckResult,
  Headers,
  HttpsCheckResult,
  LatencyCheckResult,
} from '@meshwatch/types';
import { Dictionary, isEmpty } from 'lodash';

import { UnreachableCaseError, validationBoom } from '../../shared/rest';
import BaseService from '../../shared/services';
import DateUtils from '../../util/date';
import HttpUtils from '../../util/http';
import { BASE_HTTPS_OPTIONS } from '../../util/https';

import { NodeFetch } from './types';
import { validateExecuteMonitorPayload } from './validation';

const executorTimeout = parseInt(process.env.EXECUTOR_TIMEOUT || '10000', 10);

export class MonitorExecutorService extends BaseService {
  // For some reason PeerCertificate will be empty on sequential calls to same domain
  private readonly certificateCache: Dictionary<PeerCertificate>;
  private readonly fetch: NodeFetch;

  public constructor(fetch: NodeFetch = nodeFetch) {
    super();
    this.fetch = fetch;
    this.certificateCache = {};
  }

  // 10 seconds timeout by default
  public static readonly DEFAULT_EXECUTOR_TIMEOUT = executorTimeout;

  public executeMonitorCheck = async <T extends ExecuteMonitorParamsDTO>(params: T) => {
    const validationResponse = await validateExecuteMonitorPayload(params);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }

    return this.tryExecute<ExecutorCheckResult>(async () => {
      const { endpoint, type, ...rest } = params;
      const url = parseURL(endpoint);

      switch (type) {
        case 'latency-check':
          const latencyResponse = await this.latencyCheck({ url, ...rest });
          return this.serviceResponse(latencyResponse);
        case 'certificate-check':
          const certificateResponse = await this.certificateCheck(url);
          return this.serviceResponse(certificateResponse);
        case 'https-check':
          const httpsResponse = await this.httpsCheck(url);
          return this.serviceResponse(httpsResponse);
        default:
          throw new UnreachableCaseError(type);
      }
    });
  };

  public latencyCheck({
    url,
    timeout = MonitorExecutorService.DEFAULT_EXECUTOR_TIMEOUT,
    headers,
  }: LatencyCheckParams): Promise<LatencyCheckResult> {
    return new Promise(async resolve => {
      const { href } = url;
      if (!href) {
        return resolve({ httpStatus: 400, error: `Expected url, but got \`${href}\`` });
      }

      this.fetch(href, { timeout, headers })
        .then(resp => {
          return resp.blob().then(blob => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const timings = resp.timings!;
            const blobSize = blob.size;
            resolve({
              blobSize,
              timings,
              httpStatus: resp.status,
            });
          });
        })
        .catch((err: Error) => {
          let errorMessage = err.message;
          let httpStatus = 400;
          if (err.message.includes('network timeout')) {
            errorMessage = `Latency check timed out using timeout of ${timeout}ms.`;
            httpStatus = 408; // Connection timed out
          }

          resolve({
            error: errorMessage,
            httpStatus,
          });
        });
    });
  }

  public async certificateCheck(
    url: UrlWithStringQuery,
    timeout: number = MonitorExecutorService.DEFAULT_EXECUTOR_TIMEOUT
  ): Promise<CertificateCheckResult> {
    const hostname = url.protocol ? url.hostname : url.href;
    if (!hostname) {
      return { hasCertificate: false, error: `Expected hostname, but got \`${hostname}\`` };
    }

    return new Promise<CertificateCheckResult>(resolve => {
      const timeoutId = setTimeout(() => {
        resolve({
          hasCertificate: false,
          error: `Certificate check timed out using timeout of ${timeout}ms.`,
        });
      }, timeout);

      const options: RequestOptions = { ...BASE_HTTPS_OPTIONS, hostname };
      const httpsResp = https.get(options).on('error', (err: Error) => {
        clearTimeout(timeoutId);
        const errorMessage = err.message as string;
        resolve({ hasCertificate: false, error: errorMessage });
      });

      httpsResp.on('socket', (socket: TLSSocket) => {
        socket.on('secureConnect', () => {
          let peerCertificate = socket.getPeerCertificate();
          if (isEmpty(peerCertificate)) {
            peerCertificate = this.certificateCache[hostname];
          } else {
            this.certificateCache[hostname] = peerCertificate;
          }
          const certificateCheck = this.parseCertificate(peerCertificate);
          resolve(certificateCheck);
          clearTimeout(timeoutId);
        });
      });
    });
  }

  public async httpsCheck(
    url: UrlWithStringQuery,
    timeout: number = MonitorExecutorService.DEFAULT_EXECUTOR_TIMEOUT
  ): Promise<HttpsCheckResult> {
    const hostname = url.protocol ? url.hostname : url.href;
    if (!hostname) {
      return { secure: false, error: `Expected hostname, but got \`${hostname}\`` };
    }
    return HttpUtils.timeoutableGet({ hostname }, timeout)
      .then(resp => {
        const secure = HttpUtils.hasHttpsRedirect(resp);

        return secure
          ? { secure }
          : {
              secure,
              error: `Could not find https redirect from ${hostname}, statusMessage: ${resp.statusMessage}`,
            };
      })
      .catch((err: Error) => {
        const httpsCheck: HttpsCheckResult = { secure: false, error: err.message };
        return httpsCheck;
      });
  }

  private parseCertificate(certificate: PeerCertificate): CertificateCheckResult {
    const valid_to = new Date(certificate.valid_to);
    const expires_in = DateUtils.daysDateDiff(new Date(), valid_to);
    const hasCertificate = expires_in !== undefined && expires_in > 0;

    return hasCertificate
      ? {
          hasCertificate,
          valid_to,
          expires_in,
        }
      : {
          hasCertificate,
          error: 'Could not find PeerCertificate.',
        };
  }
}

type LatencyCheckParams = {
  url: UrlWithStringQuery;
  timeout?: number;
  headers?: Headers;
};

const monitorExecutorService = new MonitorExecutorService();

export default monitorExecutorService;
